import { useFlags } from "launchdarkly-react-client-sdk";
import { Icon } from "src/components/layout/nav/icons/ad-studio";
import type { UseSectionHook } from "./types";
import { NavSection } from "src/components/layout/nav/nav-section";
import type { ActiveSectionProps } from "src/components/layout/nav/utils";
import { useIsAdStudioEnabled } from "src/pages/agents/hooks/use-is-ad-studio-enabled";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { STYLES_PATH } from "src/components/llm/context-usage/utils";

export const useAdStudioSection: UseSectionHook = () => {
  const isAdStudioEnabled = useIsAdStudioEnabled();
  const { adStudioStyles, adStudioEnrichedCatalogs } = useFlags();

  return {
    title: "Ad Studio",
    isEnabled: isAdStudioEnabled,
    links: [
      {
        isSub: true,
        href: "/ad-studio/projects",
        label: "Ads",
        activePaths: [
          "/ad-studio/ads",
          "/ad-studio/favorites",
          "/ad-studio/created-by-you",
          "/ad-studio/ideas",
          "/ad-studio/launch",
          "/ad-studio/folders",
        ],
      },
      {
        isSub: true,
        href: "/ad-studio/templates",
        label: "Templates",
      },
      ...(adStudioStyles
        ? [
            {
              isSub: true as const,
              // Styles live in Context Hub; linking there directly, not through the Ad Studio redirect, opens them over the page.
              href: STYLES_PATH,
              label: "Styles",
            },
          ]
        : []),
      ...(adStudioEnrichedCatalogs
        ? [
            {
              isSub: true as const,
              href: "/ad-studio/catalog-ads",
              label: "Catalog ads",
            },
          ]
        : []),
      {
        isSub: true,
        href: "/ad-studio/insights",
        label: "Insights",
      },
    ],
  };
};

export const AdStudio = (props: ActiveSectionProps) => {
  const section = useAdStudioSection();
  const { isPermitted: canViewAgents } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_view_agents" },
  });

  if (!canViewAgents) {
    return null;
  }

  return <NavSection icon={Icon} {...section} {...props} />;
};
